/**
 * File: pages/Onboarding/GuidedStudentOnboarding.tsx
 * Purpose: Guided wizard-style student onboarding with single-screen step switching (Web)
 * 
 * Flow:
 * 1. Invite code entry OR QR scanner (web can't scan QR, so just code entry)
 * 2. Date of Birth (required, must be 13+)
 * 3. EULA acceptance (only if age valid)
 * 4. Email/password (creates auth user, sets EULA)
 * 5. Confirm/edit first/last name
 * 6. Add avatar
 * 7. Add phone (optional)
 * 8. Confirm all details and enter app
 */

import { ArrowLeft, Eye, EyeOff, User, Image as ImageIcon, LogOut } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { OtpInput } from '../../components/ui/OtpInput';
import { ImageCropper } from '../../components/ImageCropper';
import { supabase } from '../../lib/supabase';
import {
  PASSWORD_POLICY_PLACEHOLDER,
  validatePassword,
} from '@rollops/app-shared/utils/passwordPolicy';
import { PasswordRulesList } from '../../components/auth/PasswordRulesList';

type WizardStep =
  | 'invite-entry'
  | 'dob'
  | 'eula'
  | 'email-confirm'
  | 'password'
  | 'avatar'
  | 'name-confirm'
  | 'phone'
  | 'confirm';

interface InviteData {
  companyId: string;
  locationId?: string;
  draftStudentId?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
}

export default function GuidedStudentOnboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [currentStep, setCurrentStep] = useState<WizardStep>('invite-entry');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Invite entry state
  const [inviteCode, setInviteCode] = useState(searchParams.get('code') || '');
  const [inviteData, setInviteData] = useState<InviteData | null>(null);

  // EULA state
  const [eulaAccepted, setEulaAccepted] = useState(false);

  // Email/password state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Name state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  // Phone/DOB state
  const [phone, setPhone] = useState('');
  const [birthdate, setBirthdate] = useState<string>('');
  
  // Age validation constants
  const MINIMUM_AGE = 13; // COPPA minimum age requirement

  // Avatar state
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);

  // Draft student data state
  const [draftStudentData, setDraftStudentData] = useState<{
    subscription_name?: string | null;
    subscription_price?: string | null;
    contract_start_date?: string | null;
    contract_end_date?: string | null;
    punch_card_total?: number | null;
    punch_card_remaining?: number | null;
    punch_card_limit?: number | null;
    is_trial_member?: boolean | null;
    account_status?: string | null;
    notes?: string | null;
  } | null>(null);

  // Initialize from invite data if available
  useEffect(() => {
    if (inviteData) {
      setFirstName(inviteData.firstName || '');
      setLastName(inviteData.lastName || '');
      setPhone(inviteData.phone || '');
      setEmail(inviteData.email || ''); // Pre-fill email from draft
    }
  }, [inviteData]);

  // Fetch draft student data if draftStudentId exists
  useEffect(() => {
    const fetchDraftStudentData = async () => {
      if (!inviteData?.draftStudentId) return;

      try {
        const { data, error } = await supabase
          .from('staging_import_members')
          .select('subscription_name, subscription_price, contract_start_date, contract_end_date, punch_card_total, punch_card_remaining, punch_card_limit, is_trial_member, account_status, notes')
          .eq('id', inviteData.draftStudentId)
          .single();

        if (error) {
          return;
        }

        if (data) {
          setDraftStudentData(data);
        }
      } catch {
        // Error fetching draft student data
      }
    };

    fetchDraftStudentData();
  }, [inviteData?.draftStudentId]);

  // Initialize from QR code (companyId/locationId) if provided in URL.
  // If the user is already signed in, short-circuit the signup wizard:
  //   - existing member of this gym → switch company, go to feed
  //   - signed in but not a member → create relationship, go to feed
  // Only unauthenticated visitors go through the full signup wizard.
  useEffect(() => {
    const companyId = searchParams.get('companyId');
    const locationId = searchParams.get('locationId');

    if (!companyId || inviteData || currentStep !== 'invite-entry') return;

    (async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        try {
          // Check if they're already a member of this gym.
          const { data: existingRelationship } = await supabase
            .from('user_company_relationships')
            .select('id, primary_location_id')
            .eq('user_id', session.user.id)
            .eq('company_id', companyId)
            .maybeSingle();

          if (!existingRelationship) {
            // Not a member yet — create the relationship via a system role.
            const { data: studentRole } = await supabase
              .from('system_roles')
              .select('system_role_id')
              .eq('role', 'student')
              .single();

            if (studentRole?.system_role_id) {
              await supabase.from('user_company_relationships').insert({
                user_id: session.user.id,
                company_id: companyId,
                role_id: studentRole.system_role_id,
                onboarding_complete: true,
                primary_location_id: locationId || null,
              });
            }
          } else if (locationId && existingRelationship.primary_location_id !== locationId) {
            // Update primary location if a new one was scanned.
            await supabase
              .from('user_company_relationships')
              .update({ primary_location_id: locationId, updated_at: new Date().toISOString() })
              .eq('id', existingRelationship.id);
          }

          // Switch the user's last_selected_company so JWT reflects this gym.
          await supabase
            .from('user_profiles')
            .update({ last_selected_company: companyId })
            .eq('id', session.user.id);

          await supabase.auth.refreshSession();
          navigate('/feed');
          return;
        } catch {
          // Non-fatal — fall through to signup wizard so they can still self-enroll.
        }
      }

      // Unauthenticated: initialize wizard with QR context, skip to DOB.
      setInviteData({
        companyId,
        locationId: locationId || undefined,
      });
      setCurrentStep('dob');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, currentStep]);

  // Auto-decode code if provided in URL
  useEffect(() => {
    if (inviteCode && inviteCode.length === 6 && !inviteData && currentStep === 'invite-entry') {
      handleInviteCodeSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteCode, currentStep]);

  const handleInviteCodeSubmit = async () => {
    if (!inviteCode.trim() || inviteCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const codeToDecode = inviteCode.trim().toUpperCase();
      
      // Use type assertion because RPC function may not be in TypeScript types yet
      type DecodeInviteCodeResult = {
        company_id?: string;
        location_id?: string;
        draft_student_id?: string;
        email?: string;
        status?: string;
        error?: string;
      } | null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: codeData, error: codeError } = await (supabase.rpc as any)('decode_invite_code', {
        p_code: codeToDecode,
      }) as { data: DecodeInviteCodeResult; error: unknown };

      if (codeError) {
        setError(`Failed to process invite code: ${codeError instanceof Error ? codeError.message : 'Please try again'}`);
        setLoading(false);
        return;
      }

      // Type the response as the expected JSON structure
      const decoded = codeData as {
        success?: boolean;
        found?: boolean;
        company_id?: string;
        location_id?: string;
        draft_student_id?: string;
        email?: string;
        first_name?: string;
        last_name?: string;
        phone_number?: string;
        status?: string;
        error?: string;
      } | null;

      if (!decoded) {
        setError('Invalid or expired invite code. Please check the code and try again.');
        setLoading(false);
        return;
      }

      if (!decoded.found) {
        setError(`Invalid or expired invite code. The code "${codeToDecode}" was not found in our system.`);
        setLoading(false);
        return;
      }

      if (!decoded.company_id) {
        setError('Invalid invite code: missing company information');
        setLoading(false);
        return;
      }

      setInviteData({
        companyId: decoded.company_id,
        locationId: decoded.location_id,
        draftStudentId: decoded.draft_student_id,
        firstName: decoded.first_name,
        lastName: decoded.last_name,
        phone: decoded.phone_number,
        email: decoded.email, // Store email from draft
      });

      // Pre-fill email if available
      if (decoded.email) {
        setEmail(decoded.email);
      }

      setCurrentStep('dob');
    } catch (err) {
      setError(`Failed to process invite code: ${err instanceof Error ? err.message : 'Please try again'}`);
    } finally {
      setLoading(false);
    }
  };

  // Calculate age from date of birth
  const calculateAge = (dob: string): number | null => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Validate age requirement
  const isAgeValid = (dob: string): boolean => {
    const age = calculateAge(dob);
    return age !== null && age >= MINIMUM_AGE;
  };

  const handleDobSubmit = () => {
    if (!birthdate.trim()) {
      setError('Date of birth is required');
      return;
    }

    const age = calculateAge(birthdate);
    if (age === null || age < MINIMUM_AGE) {
      setError(`You must be at least ${MINIMUM_AGE} years old to use this service`);
      return;
    }

    setError(null);
    // After DOB validation, go to EULA step
    setCurrentStep('eula');
  };

  const handleEulaAccept = () => {
    if (!eulaAccepted) {
      setError('Please accept the terms and conditions');
      return;
    }

    // Safety check: validate age again before proceeding
    if (!birthdate || !isAgeValid(birthdate)) {
      setError(`You must be at least ${MINIMUM_AGE} years old to use this service`);
      return;
    }

    // After EULA, go to email confirmation step
    setCurrentStep('email-confirm');
  };

  const handleEmailConfirm = () => {
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    // After email confirmation, go to password step
    setCurrentStep('password');
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password.trim()) {
      setError('Password is required');
      return;
    }

    const passwordCheck = validatePassword(password);
    if (!passwordCheck.ok) {
      setError(passwordCheck.reason);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!inviteData?.companyId) {
      setError('Invalid invite data. Please start over.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create auth user with EULA accepted (user record gets created here)
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: {
            signup_intent: inviteData.draftStudentId ? 'draft_student_invite' : 'student_with_invite',
            first_name: firstName.trim() || inviteData.firstName || '',
            last_name: lastName.trim() || inviteData.lastName || '',
            company_id: inviteData.companyId,
            location_id: inviteData.locationId,
            draft_student_id: inviteData.draftStudentId,
            eula_accepted: true, // EULA accepted in this flow
          },
        },
      });

      if (authError) {
        // If the email is already registered, funnel them to sign-in with the
        // QR context preserved so Signin can auto-join them to this gym.
        const msg = authError.message || '';
        const alreadyRegistered =
          msg.includes('already registered') || msg.includes('already been registered');
        if (alreadyRegistered && inviteData?.companyId) {
          const params = new URLSearchParams({
            companyId: inviteData.companyId,
            email: email.trim(),
          });
          if (inviteData.locationId) params.set('locationId', inviteData.locationId);
          setLoading(false);
          navigate(`/signin?${params.toString()}`);
          return;
        }
        setError(authError.message);
        setLoading(false);
        return;
      }

      if (!authData.user) {
        setError('Failed to create account');
        setLoading(false);
        return;
      }

      // Get the latest EULA version for students (user type)
      let eulaVersion: string | null = null;
      
      const { data: versionData, error: versionError } = await supabase
        .rpc('get_latest_eula_version', { eula_type: 'user' });

      if (versionError) {
        // Failed to get EULA version from RPC - will use fallback
      } else if (versionData) {
        eulaVersion = versionData;
      }

      // Fallback: If no version from RPC, use default version "1.0"
      // This matches mobile app behavior when no remote EULA is available
      if (!eulaVersion) {
        eulaVersion = '1.0';
      }

      // Update EULA status in user_profiles
      // Set both eula_accepted_at AND eula_version (mobile app requires both)
      const eulaUpdateData = {
        eula_accepted_at: new Date().toISOString(),
        eula_version: eulaVersion,
      };

      await supabase
        .from('user_profiles')
        .update(eulaUpdateData)
        .eq('id', authData.user.id);

      // Sign in the user
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (signInError) {
        setError('Failed to sign in. Please try again.');
        setLoading(false);
        return;
      }

      // Wait a moment for session to be fully established before proceeding
      // This ensures RLS policies can properly check auth.uid()
      await new Promise(resolve => setTimeout(resolve, 500));

      // Verify session is established
      const { data: { session: verifySession } } = await supabase.auth.getSession();
      if (!verifySession) {
        setError('Session not established. Please try again.');
        setLoading(false);
        return;
      }

      // After password creation, go to avatar step
      setCurrentStep('avatar');
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleNameConfirm = () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError('First and last name are required');
      return;
    }
    setCurrentStep('phone');
  };

  const handleAvatarContinue = () => {
    // After avatar, go to name confirmation
    setCurrentStep('name-confirm');
  };

  const handlePhoneSubmit = () => {
    setCurrentStep('confirm');
  };

  const handleFileSelect = (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (PNG, JPG, etc.)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setError(null);
    setOriginalFile(file);
    
    // Create preview URL for cropping
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageToCrop(reader.result as string);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const uploadFile = async (file: File) => {
    setAvatarUploading(true);
    setError(null);

    try {
      // Ensure we have a valid session before uploading
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.user) {
        setError('Please wait for authentication to complete. If this persists, please refresh the page.');
        setAvatarUploading(false);
        return;
      }

      const user = session.user;
      
      const fileExt = file.name.split('.').pop() || 'jpg';
      // RLS policy requires first folder to be auth.uid() - ensure path format matches
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { 
          contentType: file.type,
          upsert: true 
        });

      if (uploadError) {
        setError(`Failed to upload avatar: ${uploadError.message || 'Please try again'}`);
        setAvatarUploading(false);
        return;
      }

      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      if (urlData?.publicUrl) {
        setAvatarUrl(urlData.publicUrl);
      } else {
        setError('Failed to get avatar URL');
      }
    } catch (err) {
      setError(`Failed to upload avatar: ${err instanceof Error ? err.message : 'Please try again'}`);
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    handleFileSelect(file);
  };

  const handleCropComplete = async (croppedImageUrl: string) => {
    setShowCropper(false);
    
    // Convert blob URL to File
    const response = await fetch(croppedImageUrl);
    const blob = await response.blob();
    const file = new File([blob], originalFile?.name || 'avatar.png', {
      type: blob.type || 'image/png',
    });

    // Upload the cropped image
    await uploadFile(file);
    
    // Clean up
    URL.revokeObjectURL(croppedImageUrl);
    setImageToCrop(null);
    setOriginalFile(null);
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setImageToCrop(null);
    setOriginalFile(null);
  };


  const handleFinalSubmit = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setError('User not found');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Update profile with all collected data
      const profileUpdate: {
        first_name: string;
        last_name: string;
        phone: string | null;
        dob: string | null;
        avatar_url: string | null;
        profile_complete: boolean;
      } = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim() || null,
        dob: birthdate || null,
        avatar_url: avatarUrl,
        profile_complete: true,
      };

      const { error: updateError } = await supabase
        .from('user_profiles')
        .update(profileUpdate)
        .eq('id', user.id);

      if (updateError) {
        setError('Failed to save profile');
        setLoading(false);
        return;
      }

      // Mark onboarding complete
      if (inviteData?.companyId) {
        await supabase
          .from('user_company_relationships')
          .update({ onboarding_complete: true })
          .eq('user_id', user.id)
          .eq('company_id', inviteData.companyId);
      }

      // Navigate to student landing page
      navigate('/student-landing');
    } catch (err) {
      setError('Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'invite-entry':
        return (
          <div className="w-full max-w-md mx-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-[#EBFFEE] mb-2 text-center">
              Do you have an invite code?
            </h2>
            <p className="text-sm sm:text-base text-[#A9A3A3] mb-6 sm:mb-8 text-center">
              Enter your 6-digit code to get started
            </p>

            <div className="mb-6">
              <OtpInput
                length={6}
                alphanumeric
                onComplete={(code) => {
                  setInviteCode(code.toUpperCase());
                  // Auto-submit when complete
                  if (code.length === 6 && !loading) {
                    handleInviteCodeSubmit();
                  }
                }}
                onChange={(code) => {
                  setInviteCode(code.toUpperCase());
                  setError(null);
                }}
                error={!!error}
                disabled={loading}
              />
            </div>

            <Button
              onClick={handleInviteCodeSubmit}
              disabled={loading || inviteCode.length !== 6}
              variant="red"
              size="lg"
              className="w-full"
            >
              {loading ? 'Processing...' : 'Continue'}
            </Button>

            <div className="mt-6 text-center">
              <p className="text-sm text-[#A9A3A3]">
                Don&apos;t have a code? Contact your gym for an invitation.
              </p>
            </div>
          </div>
        );

      case 'dob':
        return (
          <div className="w-full max-w-md mx-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-[#EBFFEE] mb-2 text-center">
              Date of Birth
            </h2>
            <p className="text-sm sm:text-base text-[#A9A3A3] mb-6 sm:mb-8 text-center">
              You must be at least {MINIMUM_AGE} years old to use this service
            </p>

            <div className="space-y-4 mb-6">
              <Input
                type="date"
                placeholder="Date of Birth"
                value={birthdate}
                onChange={(e) => {
                  setBirthdate(e.target.value);
                  setError(null);
                }}
                required
                max={new Date(new Date().setFullYear(new Date().getFullYear() - MINIMUM_AGE)).toISOString().split('T')[0]}
              />
              {birthdate && calculateAge(birthdate) !== null && (
                <p className="text-sm text-[#A9A3A3] text-center">
                  Age: {calculateAge(birthdate)} years old
                </p>
              )}
            </div>

            {error && (
              <p className="text-red-500 text-sm text-center mb-4">{error}</p>
            )}

            <Button
              onClick={handleDobSubmit}
              disabled={!birthdate.trim()}
              variant="red"
              size="lg"
              className="w-full"
            >
              Continue
            </Button>
          </div>
        );

      case 'eula':
        return (
          <div className="w-full max-w-2xl mx-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-[#EBFFEE] mb-2 text-center">
              Terms & Conditions
            </h2>
            <p className="text-sm sm:text-base text-[#A9A3A3] mb-4 sm:mb-6 text-center">
              Please review and accept to continue
            </p>

            <div className="bg-[#221F1F] rounded-lg p-4 sm:p-6 mb-4 sm:mb-6 max-h-64 sm:max-h-96 overflow-y-auto">
              <p className="text-[#A9A3A3] text-sm leading-relaxed mb-4">
                By using RollOps, you agree to our Terms of Service and Privacy Policy.
                Please review the following documents before accepting:
              </p>
              <ul className="text-sm text-[#A9A3A3] space-y-3 list-disc list-inside">
                <li>You agree to use RollOps in accordance with all applicable laws and regulations</li>
                <li>You are responsible for maintaining the confidentiality of your account</li>
                <li>You agree not to share your account credentials with others</li>
                <li>You understand that RollOps may collect and use your data as described in our Privacy Policy</li>
                <li>You agree to receive communications from RollOps regarding your account and services</li>
              </ul>
              <div className="mt-4 flex flex-col gap-2">
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-rollops-accent hover:text-[#EBFFEE] underline text-sm"
                >
                  Read full Terms of Service &rarr;
                </a>
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-rollops-accent hover:text-[#EBFFEE] underline text-sm"
                >
                  Read full Privacy Policy &rarr;
                </a>
              </div>
            </div>

            <label className="flex items-start gap-3 mb-6 cursor-pointer">
              <input
                type="checkbox"
                checked={eulaAccepted}
                onChange={(e) => setEulaAccepted(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-[#504A4A] bg-[#221F1F] text-rollops-accent focus:ring-[#9B1421]"
              />
              <span className="text-[#EBFFEE] text-sm">
                I have read and agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-rollops-accent hover:text-[#EBFFEE] underline">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-rollops-accent hover:text-[#EBFFEE] underline">Privacy Policy</a>
              </span>
            </label>

            {error && (
              <p className="text-red-500 text-sm text-center mb-4">{error}</p>
            )}

            <Button
              onClick={handleEulaAccept}
              disabled={!eulaAccepted}
              variant="red"
              size="lg"
              className="w-full"
            >
              Continue
            </Button>
          </div>
        );

      case 'email-confirm':
        return (
          <div className="w-full max-w-md mx-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-[#EBFFEE] mb-2 text-center">
              Confirm Your Email
            </h2>
            <p className="text-sm sm:text-base text-[#A9A3A3] mb-6 sm:mb-8 text-center">
              Review or change your email address
            </p>

            <div className="space-y-4 mb-6">
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                maxLength={254}
                className="text-black"
              />
            </div>

            <Button
              onClick={handleEmailConfirm}
              disabled={!email.trim()}
              variant="red"
              size="lg"
              className="w-full"
            >
              Continue
            </Button>
          </div>
        );

      case 'password':
        return (
          <div className="w-full max-w-md mx-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-[#EBFFEE] mb-2 text-center">
              Create Your Password
            </h2>
            <p className="text-sm sm:text-base text-[#A9A3A3] mb-6 sm:mb-8 text-center">
              Set a secure password for your account
            </p>

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={PASSWORD_POLICY_PLACEHOLDER}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  aria-describedby="guided-password-rules"
                  className="text-black"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A9A3A3] hover:text-[#EBFFEE]"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              <PasswordRulesList id="guided-password-rules" password={password} />

              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="text-black"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A9A3A3] hover:text-[#EBFFEE]"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              {error && (
                <p className="text-red-500 text-sm text-center">{error}</p>
              )}

              <Button
                type="submit"
                disabled={loading}
                variant="red"
                size="lg"
                className="w-full"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </form>
          </div>
        );

      case 'name-confirm':
        return (
          <div className="w-full max-w-md mx-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-[#EBFFEE] mb-2 text-center">
              Confirm Your Name
            </h2>
            <p className="text-sm sm:text-base text-[#A9A3A3] mb-6 sm:mb-8 text-center">
              {firstName && lastName ? 'Review or edit your name' : 'Enter your name'}
            </p>

            <div className="space-y-4 mb-6">
              <Input
                type="text"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                maxLength={30}
              />
              <Input
                type="text"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                maxLength={30}
              />
            </div>

            <Button
              onClick={handleNameConfirm}
              disabled={!firstName.trim() || !lastName.trim()}
              variant="red"
              size="lg"
              className="w-full"
            >
              Continue
            </Button>
          </div>
        );

      case 'phone':
        return (
          <div className="w-full max-w-md mx-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-[#EBFFEE] mb-2 text-center">
              Phone Number
            </h2>
            <p className="text-sm sm:text-base text-[#A9A3A3] mb-6 sm:mb-8 text-center">
              Optional - you can add this later
            </p>

            <div className="space-y-4 mb-6">
              <Input
                type="tel"
                placeholder="Phone Number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>

            <Button
              onClick={handlePhoneSubmit}
              variant="red"
              size="lg"
              className="w-full"
            >
              Continue
            </Button>
          </div>
        );

      case 'avatar':
        return (
          <div className="w-full max-w-md mx-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-[#EBFFEE] mb-2 text-center">
              Add Your Photo
            </h2>
            <p className="text-sm sm:text-base text-[#A9A3A3] mb-6 sm:mb-8 text-center">
              Optional - you can add this later
            </p>

            <div className="flex justify-center mb-4 sm:mb-6">
              {avatarUrl ? (
                <div className="relative">
                  <img
                    src={avatarUrl}
                    alt="Avatar"
                    className="w-32 h-32 sm:w-40 sm:h-40 rounded-full object-cover border-4 border-[#9B1421]"
                  />
                  <button
                    onClick={() => setAvatarUrl(null)}
                    className="absolute -top-2 -right-2 bg-[#9B1421] rounded-full p-1"
                  >
                    <span className="text-white text-xs">×</span>
                  </button>
                </div>
              ) : (
                <label className="w-32 h-32 sm:w-40 sm:h-40 rounded-full border-2 border-dashed border-[#9B1421] bg-[#221F1F] flex flex-col items-center justify-center cursor-pointer hover:bg-[#363232] transition-colors">
                  <ImageIcon size={40} className="sm:w-12 sm:h-12 text-rollops-accent mb-2" />
                  <span className="text-xs sm:text-sm text-[#A9A3A3]">Tap to upload</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    className="hidden"
                    disabled={avatarUploading}
                  />
                </label>
              )}
            </div>

            {avatarUploading && (
              <p className="text-center text-[#A9A3A3] mb-4">Uploading...</p>
            )}

            <Button
              onClick={handleAvatarContinue}
              variant="red"
              size="lg"
              className="w-full"
            >
              Continue
            </Button>

            {/* Image Cropper Modal */}
            {showCropper && imageToCrop && (
              <ImageCropper
                key={imageToCrop} // Force remount when image changes to reset cropper state
                image={imageToCrop}
                onCropComplete={handleCropComplete}
                onCancel={handleCropCancel}
                aspectRatio={1}
                cropShape="round"
                minZoom={1}
                maxZoom={3}
              />
            )}
          </div>
        );

      case 'confirm':
        return (
          <div className="w-full max-w-md mx-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-[#EBFFEE] mb-2 text-center">
              Review Your Details
            </h2>
            <p className="text-sm sm:text-base text-[#A9A3A3] mb-6 sm:mb-8 text-center">
              Confirm everything looks correct
            </p>

            {/* Profile-style layout */}
            <div className="flex flex-col items-center mb-6 sm:mb-8">
              {/* Avatar - Top Center */}
              <div className="mb-4 sm:mb-6">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="Profile"
                    className="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover border-4 border-[#504A4A] shadow-lg"
                  />
                ) : (
                  <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-[#363232] flex items-center justify-center border-4 border-[#504A4A] shadow-lg">
                    <User size={48} className="sm:w-16 sm:h-16 text-[#A9A3A3]" />
                  </div>
                )}
              </div>

              {/* Name - Centered below avatar */}
              <h3 className="text-2xl sm:text-3xl font-bold text-[#EBFFEE] mb-6 sm:mb-8 text-center">
                {firstName} {lastName}
              </h3>

              {/* Details - Centered below name */}
              <div className="w-full space-y-3 mb-8">
                <div className="text-center">
                  <p className="text-sm text-[#A9A3A3] mb-1">Email</p>
                  <p className="text-[#EBFFEE] font-medium">{email}</p>
                </div>

                {phone && (
                  <div className="text-center">
                    <p className="text-sm text-[#A9A3A3] mb-1">Phone</p>
                    <p className="text-[#EBFFEE] font-medium">{phone}</p>
                  </div>
                )}

                {birthdate && (
                  <div className="text-center">
                    <p className="text-sm text-[#A9A3A3] mb-1">Date of Birth</p>
                    <p className="text-[#EBFFEE] font-medium">
                      {new Date(birthdate).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>

              {/* Draft Student Data - Subscription & Attendance Details */}
              {draftStudentData && (
                <div className="w-full space-y-3 sm:space-y-4 mb-6 sm:mb-8 border-t border-[#504A4A] pt-4 sm:pt-6">
                  <h4 className="text-base sm:text-lg font-semibold text-[#EBFFEE] text-center mb-3 sm:mb-4">
                    Your Membership Details
                  </h4>

                  {/* Subscription Details */}
                  {draftStudentData.subscription_name && (
                    <div className="bg-[#1A1818] rounded-lg p-3 sm:p-4 border border-[#504A4A]">
                      <div className="text-center space-y-2">
                        <p className="text-sm text-[#A9A3A3]">Subscription</p>
                        <p className="text-[#EBFFEE] font-medium text-lg">
                          {draftStudentData.subscription_name}
                        </p>
                        {draftStudentData.subscription_price && (
                          <p className="text-[#EBFFEE] text-sm">
                            ${parseFloat(draftStudentData.subscription_price).toFixed(2)}
                            {draftStudentData.is_trial_member && (
                              <span className="text-[#A9A3A3] ml-2">(Trial)</span>
                            )}
                          </p>
                        )}
                        {draftStudentData.contract_start_date && draftStudentData.contract_end_date && (
                          <p className="text-xs text-[#A9A3A3] mt-2">
                            {new Date(draftStudentData.contract_start_date).toLocaleDateString()} - {new Date(draftStudentData.contract_end_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Punch Card / Attendance Data */}
                  {(draftStudentData.punch_card_total !== null || draftStudentData.punch_card_limit !== null) && (
                    <div className="bg-[#1A1818] rounded-lg p-3 sm:p-4 border border-[#504A4A]">
                      <div className="text-center space-y-2">
                        <p className="text-sm text-[#A9A3A3]">Punch Card</p>
                        {draftStudentData.punch_card_limit !== null && (
                          <div className="flex items-center justify-center gap-1.5 sm:gap-2 flex-wrap">
                            {draftStudentData.punch_card_remaining !== null && (
                              <p className="text-xl sm:text-2xl font-bold text-[#EBFFEE]">
                                {draftStudentData.punch_card_remaining}
                              </p>
                            )}
                            <p className="text-[#A9A3A3]">/</p>
                            <p className="text-[#EBFFEE] font-medium">
                              {draftStudentData.punch_card_limit}
                            </p>
                            <p className="text-xs sm:text-sm text-[#A9A3A3] ml-1">classes remaining</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Account Status */}
                  {draftStudentData.account_status && (
                    <div className="text-center">
                      <p className="text-sm text-[#A9A3A3] mb-1">Account Status</p>
                      <p className="text-[#EBFFEE] font-medium capitalize">
                        {draftStudentData.account_status}
                      </p>
                    </div>
                  )}

                  {/* TODO: Contact Owner Feature */}
                  {/* 
                    PINNED TODO: Add ability to contact owner if data is incorrect
                    Requirements:
                    - Should NOT lose progress when contacting owner
                    - Could be a modal or inline form
                    - Should send message to company owner/admin
                    - User should be able to continue onboarding after sending message
                    - Consider: Save draft state before opening contact form
                  */}
                  <div className="text-center pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        // TODO: Implement contact owner functionality
                        // This should open a modal/form to send message to owner
                        // without losing onboarding progress
                        // TODO: Implement contact owner feature
                      }}
                      className="text-sm text-rollops-accent hover:text-[#EBFFEE] underline transition-colors"
                    >
                      Something looks wrong? Contact your gym
                    </button>
                  </div>
                </div>
              )}
            </div>

            <Button
              onClick={handleFinalSubmit}
              disabled={loading}
              variant="red"
              size="lg"
              className="w-full"
            >
              {loading ? 'Completing...' : 'Enter the App'}
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#111010] flex flex-col font-['Inter']">
      <Helmet>
        <title>Student Onboarding - RollOps</title>
      </Helmet>

      {/* Header with Navigation */}
      <header className="border-b border-[#504A4A] bg-[#111010]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1.5 sm:gap-2">
            <ArrowLeft size={18} className="sm:w-5 sm:h-5 text-[#A9A3A3]" />
            <img src="/rollops-logo.png" alt="RollOps" className="w-8 h-8 sm:w-10 sm:h-10" />
            <span className="text-xl sm:text-2xl font-bold text-[#EBFFEE]">
              Roll<span className="text-rollops-accent">O</span>ps
            </span>
          </Link>
          <button
            onClick={handleSignOut}
            className="flex items-center gap-1.5 sm:gap-2 text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
          >
            <LogOut size={16} className="sm:w-[18px] sm:h-[18px]" />
            <span className="text-xs sm:text-sm">Sign Out</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        <Card className="w-full max-w-2xl border-[#504A4A] bg-[#221F1F] shadow-lg">
          <CardContent className="p-4 sm:p-6 lg:p-8">
            {error && (
              <div className="mb-6 bg-[#9B1421]/20 border border-[#9B1421] rounded-lg p-4">
                <p className="text-text-primary text-sm text-center">{error}</p>
              </div>
            )}
            {renderStep()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

